import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import { get, put } from '../../helpers/api_helper';
import WarehouseMapDesignerCanvas from '../../components/Warehouse/WarehouseMapDesignerCanvas';
import WarehouseMapFloatingWindow from '../../components/Warehouse/WarehouseMapFloatingWindow';

const MAX_AXIS_CELLS = 100;
const MAX_AXIS_PATTERNS = 50;
const MIN_AXIS_SIZE = 0.1;
const MAX_AXIS_SIZE = 100;
const ANNOTATION_COLORS = [
  { label: 'Amber', value: '#f59e0b' },
  { label: 'Blue', value: '#2563eb' },
  { label: 'Green', value: '#16a34a' },
  { label: 'Red', value: '#dc2626' },
  { label: 'Purple', value: '#9333ea' },
];

function createAxisId(axis) {
  return `${axis.slice(0, -1)}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function createAxisEntry(axis) {
  return {
    id: createAxisId(axis),
    label: '',
    pattern: [],
    size: 1,
  };
}

function createAnnotationId() {
  return `annotation-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function parseAxisSize(value) {
  const size = Number(value);
  return Number.isFinite(size) && size >= MIN_AXIS_SIZE && size <= MAX_AXIS_SIZE
    ? size
    : null;
}

function normalizePatterns(value) {
  const entries = Array.isArray(value) ? value : [value];
  return [...new Set(entries
    .map((pattern) => (typeof pattern === 'object' ? pattern?.value : pattern))
    .filter((pattern) => typeof pattern === 'string')
    .map((pattern) => pattern.trim())
    .filter(Boolean))];
}

function createPatternEditor(value = '') {
  return {
    id: `pattern-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
    value,
  };
}

function getEditablePatterns(value) {
  let patterns = [];
  if (Array.isArray(value)) {
    patterns = value.filter((pattern) => typeof pattern === 'string');
  } else if (typeof value === 'string' && value) {
    patterns = [value];
  }
  return (patterns.length > 0 ? patterns : ['']).map(createPatternEditor);
}

function hasPatterns(entry) {
  return normalizePatterns(entry?.pattern).length > 0;
}

function resizeAxis(entries, count, axis) {
  if (count === entries.length) return entries;
  if (count < entries.length) return entries.slice(0, count);

  return [
    ...entries,
    ...Array.from({ length: count - entries.length }, () => createAxisEntry(axis)),
  ];
}

function pruneGridReferences(definition, columns, rows) {
  const columnIds = new Set(columns.map((entry) => entry.id));
  const rowIds = new Set(rows.map((entry) => entry.id));
  const { routeHome } = definition;

  return {
    ...definition,
    schemaVersion: 5,
    columns,
    rows,
    walkableCells: (definition.walkableCells || []).filter((cell) => (
      columnIds.has(cell.columnId) && rowIds.has(cell.rowId)
    )),
    annotations: (definition.annotations || []).filter((annotation) => (
      columnIds.has(annotation.columnStartId)
      && columnIds.has(annotation.columnEndId)
      && rowIds.has(annotation.rowStartId)
      && rowIds.has(annotation.rowEndId)
    )),
    routeHome: routeHome
      && columnIds.has(routeHome.columnId)
      && rowIds.has(routeHome.rowId)
      ? routeHome
      : null,
  };
}

function definitionReferencesAxisIds(definition, columnIds, rowIds) {
  const removedColumnIds = new Set(columnIds);
  const removedRowIds = new Set(rowIds);

  return (definition.walkableCells || []).some((cell) => (
    removedColumnIds.has(cell.columnId) || removedRowIds.has(cell.rowId)
  )) || (definition.annotations || []).some((annotation) => (
    removedColumnIds.has(annotation.columnStartId)
    || removedColumnIds.has(annotation.columnEndId)
    || removedRowIds.has(annotation.rowStartId)
    || removedRowIds.has(annotation.rowEndId)
  )) || (
    definition.routeHome
    && (
      removedColumnIds.has(definition.routeHome.columnId)
      || removedRowIds.has(definition.routeHome.rowId)
    )
  );
}

function WarehouseMap({ t }) {
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [selection, setSelection] = useState(null);
  const [editor, setEditor] = useState(null);
  const [tool, setTool] = useState('select');
  const [annotationEditor, setAnnotationEditor] = useState(null);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);
  const [floatingMapOpen, setFloatingMapOpen] = useState(false);
  const [dimensionDraft, setDimensionDraft] = useState({ columns: 0, rows: 0 });
  const [testLabel, setTestLabel] = useState('');
  const [loadError, setLoadError] = useState('');
  const [pickrouteId, setPickrouteId] = useState('');
  const [routePreview, setRoutePreview] = useState(null);
  const [routePreviewIndex, setRoutePreviewIndex] = useState(-1);
  const [routePreviewLoading, setRoutePreviewLoading] = useState(false);
  const routeAnimationRef = useRef(null);

  const routePreviewStopList = useMemo(() => {
    if (!routePreview?.pickStops) return '';
    return routePreview.pickStops.flatMap((stop) => (
      stop.locations.map((location) => `${stop.sequence}: ${location}`)
    )).join('\n');
  }, [routePreview]);

  const loadMap = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const loaded = await get('/api/warehouse/map');
      setMap(loaded);
      setDimensionDraft({
        columns: loaded.definition.columns.length,
        rows: loaded.definition.rows.length,
      });
      setDirty(false);
    } catch (error) {
      setLoadError(error.response?.data?.error || error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMap();
  }, []);

  useEffect(() => () => {
    if (routeAnimationRef.current) clearInterval(routeAnimationRef.current);
  }, []);

  const stopRouteAnimation = () => {
    if (routeAnimationRef.current) {
      clearInterval(routeAnimationRef.current);
      routeAnimationRef.current = null;
    }
  };

  const clearRoutePreview = () => {
    stopRouteAnimation();
    setRoutePreview(null);
    setRoutePreviewIndex(-1);
  };

  const copyRoutePreviewStopList = async () => {
    try {
      await navigator.clipboard.writeText(routePreviewStopList);
      toastr.success(t('Sorted stop list copied'));
    } catch (error) {
      toastr.error(t('Could not copy the sorted stop list. Select the list and copy it manually.'));
    }
  };

  const previewPickroute = async (event) => {
    event.preventDefault();
    const numericId = Number(pickrouteId);
    if (!Number.isInteger(numericId) || numericId <= 0) {
      toastr.error(t('Enter a valid pick route ID.'));
      return;
    }

    stopRouteAnimation();
    setRoutePreviewLoading(true);
    setRoutePreview(null);
    setRoutePreviewIndex(-1);
    try {
      const result = await get(`/api/warehouse/map/route/pickroute/${numericId}`);
      const pickStops = result.stops.filter((stop) => stop.type === 'pick');
      setRoutePreview({ ...result, pickStops });

      if (pickStops.length > 0) {
        setRoutePreviewIndex(0);
        if (pickStops.length > 1) {
          routeAnimationRef.current = setInterval(() => {
            setRoutePreviewIndex((current) => {
              if (current >= pickStops.length - 1) {
                stopRouteAnimation();
                return current;
              }
              return current + 1;
            });
          }, 1000);
        }
      }
    } catch (error) {
      toastr.error(error.response?.data?.error || error.message);
    } finally {
      setRoutePreviewLoading(false);
    }
  };

  const selectedEntry = useMemo(() => {
    if (!map || !selection) return null;
    return map.definition[selection.axis].find((entry) => entry.id === selection.id) || null;
  }, [map, selection]);

  const selectedCoordinate = useMemo(() => {
    if (!map || !selection) return null;
    const index = map.definition[selection.axis].findIndex(
      (entry) => entry.id === selection.id,
    );
    if (index < 0) return null;
    return {
      axis: selection.axis === 'columns' ? 'X' : 'Y',
      value: index,
    };
  }, [map, selection]);

  const openAxisEditor = (axis, id) => {
    const entry = map.definition[axis].find((candidate) => candidate.id === id);
    setSelection({ axis, id });
    setEditor({
      axis,
      id,
      label: entry?.label || '',
      patterns: getEditablePatterns(entry?.pattern),
      size: entry?.size ?? 1,
    });
    setTestLabel('');
  };

  const closeEditor = () => setEditor(null);

  const updateAxisPattern = (index, value) => {
    setEditor((current) => ({
      ...current,
      patterns: current.patterns.map((pattern, patternIndex) => (
        patternIndex === index ? { ...pattern, value } : pattern
      )),
    }));
  };

  const addAxisPattern = () => {
    setEditor((current) => ({
      ...current,
      patterns: current.patterns.length >= MAX_AXIS_PATTERNS
        ? current.patterns
        : [...current.patterns, createPatternEditor()],
    }));
  };

  const removeAxisPattern = (index) => {
    setEditor((current) => {
      const patterns = current.patterns.filter((pattern, patternIndex) => (
        patternIndex !== index
      ));
      return {
        ...current,
        patterns: patterns.length > 0 ? patterns : [createPatternEditor()],
      };
    });
  };

  const updateDefinition = (updater) => {
    setMap((current) => ({
      ...current,
      definition: updater(current.definition),
    }));
    setDirty(true);
  };

  const saveAxisRule = () => {
    const patterns = normalizePatterns(editor.patterns);
    const size = parseAxisSize(editor.size);
    if (size === null) {
      toastr.error(t('Axis size must be a number between 0.1 and 100.'));
      return;
    }
    for (let index = 0; index < patterns.length; index += 1) {
      try {
        RegExp(patterns[index], 'i');
      } catch (error) {
        toastr.error(`${t('Invalid regular expression')} ${index + 1}: ${error.message}`);
        return;
      }
    }

    updateDefinition((definition) => ({
      ...definition,
      [editor.axis]: definition[editor.axis].map((entry) => (
        entry.id === editor.id
          ? {
            ...entry, label: editor.label.trim(), pattern: patterns, size,
          }
          : entry
      )),
    }));
    closeEditor();
  };

  const openAxisEditorAfterInsert = (axis, entry) => {
    setSelection({ axis, id: entry.id });
    setEditor({
      axis, id: entry.id, label: '', patterns: [createPatternEditor()], size: 1,
    });
    setTestLabel('');
  };

  const insertAxis = (position) => {
    const { axis } = editor;
    const entries = map.definition[axis];
    const size = parseAxisSize(editor.size);
    if (size === null) {
      toastr.error(t('Axis size must be a number between 0.1 and 100.'));
      return;
    }
    if (entries.length >= MAX_AXIS_CELLS) {
      toastr.error(t('The grid supports up to 100 rows or columns.'));
      return;
    }

    const currentIndex = entries.findIndex((entry) => entry.id === editor.id);
    const insertionIndex = position === 'before' ? currentIndex : currentIndex + 1;
    const inserted = createAxisEntry(axis);

    updateDefinition((definition) => {
      const next = definition[axis].map((entry) => (
        entry.id === editor.id
          ? {
            ...entry,
            label: editor.label.trim(),
            pattern: normalizePatterns(editor.patterns),
            size,
          }
          : entry
      ));
      next.splice(insertionIndex, 0, inserted);
      return { ...definition, [axis]: next };
    });
    setDimensionDraft((current) => ({ ...current, [axis]: entries.length + 1 }));
    setSelection({ axis, id: inserted.id });
    setEditor({
      axis, id: inserted.id, label: '', patterns: [createPatternEditor()], size: 1,
    });
    setTestLabel('');
  };

  const removeAxis = () => {
    const { axis } = editor;
    const entries = map.definition[axis];
    if (entries.length <= 1) {
      toastr.error(t('The map must contain at least one row and one column.'));
      return;
    }

    const removesMapData = definitionReferencesAxisIds(
      map.definition,
      axis === 'columns' ? [editor.id] : [],
      axis === 'rows' ? [editor.id] : [],
    );
    if (
      (normalizePatterns(editor.patterns).length > 0 || editor.label || removesMapData)
      // eslint-disable-next-line no-alert
      && !window.confirm(t('Remove this grid edge and any walkable cells, annotations, or Route Home attached to it?'))
    ) return;

    updateDefinition((definition) => {
      const nextAxis = definition[axis].filter((entry) => entry.id !== editor.id);
      const columns = axis === 'columns' ? nextAxis : definition.columns;
      const rows = axis === 'rows' ? nextAxis : definition.rows;
      return pruneGridReferences(definition, columns, rows);
    });
    setDimensionDraft((current) => ({ ...current, [axis]: entries.length - 1 }));
    setSelection(null);
    closeEditor();
  };

  const addAxisAtEnd = (axis) => {
    const entries = map.definition[axis];
    if (entries.length >= MAX_AXIS_CELLS) {
      toastr.error(t('The grid supports up to 100 rows or columns.'));
      return;
    }

    const inserted = createAxisEntry(axis);
    updateDefinition((definition) => ({
      ...definition,
      [axis]: [...definition[axis], inserted],
    }));
    setDimensionDraft((current) => ({ ...current, [axis]: entries.length + 1 }));
    openAxisEditorAfterInsert(axis, inserted);
  };

  const applyDimensions = () => {
    const columnCount = Number(dimensionDraft.columns);
    const rowCount = Number(dimensionDraft.rows);

    if (
      !Number.isInteger(columnCount) || columnCount < 1 || columnCount > MAX_AXIS_CELLS
      || !Number.isInteger(rowCount) || rowCount < 1 || rowCount > MAX_AXIS_CELLS
    ) {
      toastr.error(t('Rows and columns must be whole numbers between 1 and 100.'));
      return;
    }

    const removedEntries = [
      ...map.definition.columns.slice(columnCount),
      ...map.definition.rows.slice(rowCount),
    ];
    const removedColumnIds = map.definition.columns.slice(columnCount).map((entry) => entry.id);
    const removedRowIds = map.definition.rows.slice(rowCount).map((entry) => entry.id);
    const removesMapData = definitionReferencesAxisIds(
      map.definition,
      removedColumnIds,
      removedRowIds,
    );
    if (
      (removedEntries.some((entry) => entry.label || hasPatterns(entry)) || removesMapData)
      // eslint-disable-next-line no-alert
      && !window.confirm(t('Reducing the dimensions will remove configured edges, walkable cells, annotations, or Route Home from the end. Continue?'))
    ) return;

    updateDefinition((definition) => pruneGridReferences(
      definition,
      resizeAxis(definition.columns, columnCount, 'columns'),
      resizeAxis(definition.rows, rowCount, 'rows'),
    ));
    setSelection(null);
  };

  const paintCell = (rowId, columnId, walkable) => {
    updateDefinition((definition) => {
      const current = definition.walkableCells || [];
      const exists = current.some((cell) => (
        cell.rowId === rowId && cell.columnId === columnId
      ));

      if (walkable && !exists) {
        return {
          ...definition,
          schemaVersion: 5,
          walkableCells: [...current, {
            rowId,
            columnId,
            type: 'passage',
            costMultiplier: 1,
          }],
        };
      }

      if (!walkable && exists) {
        const removesRouteHome = definition.routeHome?.rowId === rowId
          && definition.routeHome?.columnId === columnId;
        return {
          ...definition,
          schemaVersion: 5,
          walkableCells: current.filter((cell) => (
            cell.rowId !== rowId || cell.columnId !== columnId
          )),
          routeHome: removesRouteHome ? null : definition.routeHome,
        };
      }

      return definition;
    });
  };

  const setRouteHome = (rowId, columnId) => {
    const walkable = (map.definition.walkableCells || []).some((cell) => (
      cell.rowId === rowId && cell.columnId === columnId
    ));
    if (!walkable) {
      toastr.error(t('Route Home must be placed on a walkable cell.'));
      return;
    }

    updateDefinition((definition) => ({
      ...definition,
      routeHome: { columnId, rowId },
      schemaVersion: 5,
    }));
    setTool('select');
  };

  const createAnnotation = (range) => {
    const annotation = {
      ...range,
      id: createAnnotationId(),
      title: '',
      note: '',
      color: ANNOTATION_COLORS[0].value,
    };
    setSelectedAnnotationId(annotation.id);
    setAnnotationEditor(annotation);
  };

  const editAnnotation = (id) => {
    const annotation = (map.definition.annotations || []).find((entry) => entry.id === id);
    if (!annotation) return;
    setSelectedAnnotationId(id);
    setAnnotationEditor({ ...annotation });
  };

  const closeAnnotationEditor = () => setAnnotationEditor(null);

  const saveAnnotation = () => {
    const annotation = {
      ...annotationEditor,
      title: annotationEditor.title.trim(),
      note: annotationEditor.note.trim(),
    };
    updateDefinition((definition) => {
      const annotations = definition.annotations || [];
      const exists = annotations.some((entry) => entry.id === annotation.id);
      return {
        ...definition,
        schemaVersion: 5,
        annotations: exists
          ? annotations.map((entry) => (entry.id === annotation.id ? annotation : entry))
          : [...annotations, annotation],
      };
    });
    setTool('select');
    closeAnnotationEditor();
  };

  const removeAnnotation = () => {
    updateDefinition((definition) => ({
      ...definition,
      annotations: (definition.annotations || []).filter(
        (entry) => entry.id !== annotationEditor.id,
      ),
    }));
    setSelectedAnnotationId(null);
    closeAnnotationEditor();
  };

  const saveMap = async () => {
    setSaving(true);
    try {
      const saved = await put('/api/warehouse/map', {
        name: map.name,
        definition: map.definition,
      });
      setMap(saved);
      setDirty(false);
      toastr.success(t('Warehouse map saved'));
    } catch (error) {
      toastr.error(error.response?.data?.error || error.message);
    } finally {
      setSaving(false);
    }
  };

  const patternTest = useMemo(() => {
    const patterns = normalizePatterns(editor?.patterns);
    if (patterns.length === 0 || !testLabel) return null;
    return patterns.some((pattern) => {
      try {
        return new RegExp(pattern, 'i').test(testLabel);
      } catch (error) {
        return false;
      }
    });
  }, [editor?.patterns, testLabel]);

  if (loading) {
    return <div className="page-content"><div className="container-fluid">{t('Loading warehouse map...')}</div></div>;
  }

  if (!map) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Alert color="danger">{loadError || t('Could not load the warehouse map.')}</Alert>
          <Button color="primary" onClick={loadMap}>{t('Try Again')}</Button>
        </div>
      </div>
    );
  }

  const renderMapWorkspace = (fillAvailable = false) => (
    <div className={`warehouse-map-designer__workspace${fillAvailable ? ' warehouse-map-designer__workspace--fill' : ''}`}>
      <form className="warehouse-map-designer__route-preview mb-3" onSubmit={previewPickroute}>
        <Row className="g-2 align-items-end">
          <Col lg="3" md="5">
            <Label for="warehouse-map-pickroute-id">{t('Test Pick Route ID')}</Label>
            <Input
              id="warehouse-map-pickroute-id"
              min="1"
              placeholder={t('Enter pick route ID')}
              type="number"
              value={pickrouteId}
              onChange={(event) => setPickrouteId(event.target.value)}
            />
          </Col>
          <Col lg="5" md="7" className="d-flex flex-wrap gap-2">
            <Button color="info" disabled={routePreviewLoading} type="submit">
              <i className="mdi mdi-play me-1" />
              {routePreviewLoading ? t('Computing Route...') : t('Preview Sorted Route')}
            </Button>
            {routePreview && (
              <Button color="secondary" outline type="button" onClick={clearRoutePreview}>
                {t('Clear Preview')}
              </Button>
            )}
          </Col>
          <Col lg="4">
            {routePreview && routePreview.pickStops.length > 0 && (
              <div className="warehouse-map-designer__route-preview-status">
                <strong>
                  {`${t('Stop')} ${routePreviewIndex + 1} ${t('of')} ${routePreview.pickStops.length}`}
                </strong>
                <span>
                  {routePreview.pickStops[routePreviewIndex]?.locations.join(', ')}
                </span>
                <small>
                  {`${Number(routePreview.totalCost).toLocaleString(undefined, {
                    maximumFractionDigits: 1,
                  })} ${t('map units')} · ${routePreview.totalSteps} ${t('cell transitions')} · ${routePreview.method}`}
                </small>
              </div>
            )}
          </Col>
        </Row>
        {routePreviewStopList && (
          <div className="warehouse-map-designer__route-stop-list mt-3">
            <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
              <Label className="mb-0" for="warehouse-map-route-stop-list">
                {t('Sorted Stops')}
              </Label>
              <Button color="secondary" outline size="sm" type="button" onClick={copyRoutePreviewStopList}>
                <i className="mdi mdi-content-copy me-1" />
                {t('Copy List')}
              </Button>
            </div>
            <Input
              id="warehouse-map-route-stop-list"
              readOnly
              rows={Math.min(12, Math.max(4, routePreviewStopList.split('\n').length))}
              type="textarea"
              value={routePreviewStopList}
            />
          </div>
        )}
      </form>

      <div className="warehouse-map-designer__toolbar d-flex flex-wrap align-items-center gap-2 mb-3">
        <span className="text-uppercase text-muted small fw-semibold me-1">
          {t('Canvas Tool')}
        </span>
        <ButtonGroup>
          <Button
            color={tool === 'select' ? 'primary' : 'secondary'}
            outline={tool !== 'select'}
            onClick={() => setTool('select')}
          >
            <i className="mdi mdi-cursor-default-outline me-1" />
            {t('Select')}
          </Button>
          <Button
            color={tool === 'paint' ? 'primary' : 'secondary'}
            outline={tool !== 'paint'}
            onClick={() => setTool('paint')}
          >
            <i className="mdi mdi-brush me-1" />
            {t('Paint Walkable')}
          </Button>
          <Button
            color={tool === 'erase' ? 'primary' : 'secondary'}
            outline={tool !== 'erase'}
            onClick={() => setTool('erase')}
          >
            <i className="mdi mdi-eraser me-1" />
            {t('Erase')}
          </Button>
          <Button
            color={tool === 'annotate' ? 'primary' : 'secondary'}
            outline={tool !== 'annotate'}
            onClick={() => setTool('annotate')}
          >
            <i className="mdi mdi-vector-rectangle me-1" />
            {t('Highlight Area')}
          </Button>
          <Button
            color={tool === 'home' ? 'primary' : 'secondary'}
            outline={tool !== 'home'}
            onClick={() => setTool('home')}
          >
            <i className="mdi mdi-home-map-marker me-1" />
            {t('Set Route Home')}
          </Button>
        </ButtonGroup>
      </div>

      <Alert color="info" className="warehouse-map-designer__help py-2">
        {tool === 'paint' && t('Drag across grid cells to mark walkable passages.')}
        {tool === 'erase' && t('Drag across grid cells to remove walkable passages.')}
        {tool === 'annotate' && t('Drag a rectangle over the grid to add a human-readable highlight and note.')}
        {tool === 'home' && t('Click a walkable cell to make it the default start and end point for routes.')}
        {tool === 'select' && t('Click an annotation to edit it, or click a top or left edge cell to configure its regex.')}
      </Alert>

      <WarehouseMapDesignerCanvas
        definition={map.definition}
        fillAvailable={fillAvailable}
        onCreateAnnotation={createAnnotation}
        onPaintCell={paintCell}
        onSelectAnnotation={editAnnotation}
        onSelectAxis={openAxisEditor}
        onSetRouteHome={setRouteHome}
        selectedAnnotationId={selectedAnnotationId}
        selection={selection}
        tool={tool}
        routePreviewIndex={routePreviewIndex}
        routePreviewStops={routePreview?.pickStops || []}
      />

      <div className="warehouse-map-designer__status d-flex flex-wrap gap-3 mt-3 text-muted small">
        <span>
          <span className="warehouse-map-designer__legend warehouse-map-designer__legend--configured" />
          {' '}
          {t('Regex configured')}
        </span>
        <span>
          <span className="warehouse-map-designer__legend warehouse-map-designer__legend--selected" />
          {' '}
          {t('Selected')}
        </span>
        <span>
          <span className="warehouse-map-designer__legend warehouse-map-designer__legend--walkable" />
          {' '}
          {`${(map.definition.walkableCells || []).length} ${t('walkable cells')}`}
        </span>
        <span>
          <span className="warehouse-map-designer__legend warehouse-map-designer__legend--annotation" />
          {' '}
          {`${(map.definition.annotations || []).length} ${t('annotations')}`}
        </span>
        <span>
          <span className="warehouse-map-designer__legend warehouse-map-designer__legend--home" />
          {' '}
          {map.definition.routeHome ? t('Route Home set') : t('Route Home not set')}
        </span>
        <span>{`${map.definition.columns.filter(hasPatterns).length}/${map.definition.columns.length} ${t('columns configured')}`}</span>
        <span>{`${map.definition.rows.filter(hasPatterns).length}/${map.definition.rows.length} ${t('rows configured')}`}</span>
      </div>
    </div>
  );

  return (
    <div className="page-content warehouse-map-designer">
      <div className="container-fluid">
        <Row>
          <Col>
            <Card>
              <CardHeader className="bg-transparent border-bottom">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div>
                    <div className="text-uppercase text-muted small">{t('Warehouse')}</div>
                    <h4 className="mb-0">{t('Map Designer')}</h4>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {dirty && <span className="badge bg-warning text-dark">{t('Unsaved changes')}</span>}
                    <Button color="primary" disabled={!dirty || saving} onClick={saveMap}>
                      {saving ? t('Saving...') : t('Save Map')}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <Row className="g-3 align-items-end mb-3">
                  <Col lg="3" md="6">
                    <Label for="warehouse-map-name">{t('Map Name')}</Label>
                    <Input
                      id="warehouse-map-name"
                      value={map.name}
                      onChange={(event) => {
                        setMap((current) => ({ ...current, name: event.target.value }));
                        setDirty(true);
                      }}
                    />
                  </Col>
                  <Col lg="2" md="3" xs="6">
                    <Label for="warehouse-map-columns">{t('X Cells')}</Label>
                    <Input
                      id="warehouse-map-columns"
                      max={MAX_AXIS_CELLS}
                      min="1"
                      type="number"
                      value={dimensionDraft.columns}
                      onChange={(event) => setDimensionDraft((current) => ({
                        ...current,
                        columns: event.target.value,
                      }))}
                    />
                  </Col>
                  <Col lg="2" md="3" xs="6">
                    <Label for="warehouse-map-rows">{t('Y Cells')}</Label>
                    <Input
                      id="warehouse-map-rows"
                      max={MAX_AXIS_CELLS}
                      min="1"
                      type="number"
                      value={dimensionDraft.rows}
                      onChange={(event) => setDimensionDraft((current) => ({
                        ...current,
                        rows: event.target.value,
                      }))}
                    />
                  </Col>
                  <Col lg="5" className="d-flex flex-wrap gap-2">
                    <Button color="secondary" outline onClick={applyDimensions}>{t('Apply Dimensions')}</Button>
                    <Button color="secondary" outline onClick={() => addAxisAtEnd('columns')}>
                      <i className="mdi mdi-table-column-plus-after me-1" />
                      {t('Add Column at End')}
                    </Button>
                    <Button color="secondary" outline onClick={() => addAxisAtEnd('rows')}>
                      <i className="mdi mdi-table-row-plus-after me-1" />
                      {t('Add Row at End')}
                    </Button>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end mb-3">
                  <Button color="secondary" outline onClick={() => setFloatingMapOpen(true)}>
                    <i className="mdi mdi-open-in-new me-1" />
                    {t('Open Floating Map')}
                  </Button>
                </div>

                {floatingMapOpen ? (
                  <div className="warehouse-map-designer__floating-placeholder">
                    <i className="mdi mdi-dock-window me-2" />
                    <span>{t('The map is open in a movable, resizable window.')}</span>
                    <Button color="link" onClick={() => setFloatingMapOpen(false)}>
                      {t('Return Map Here')}
                    </Button>
                  </div>
                ) : renderMapWorkspace()}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      {floatingMapOpen && (
        <WarehouseMapFloatingWindow
          dockLabel={t('Dock Map')}
          onDock={() => setFloatingMapOpen(false)}
          title={t('Warehouse Map')}
        >
          {renderMapWorkspace(true)}
        </WarehouseMapFloatingWindow>
      )}

      <Modal isOpen={editor !== null} toggle={closeEditor} zIndex={1080} centered>
        <ModalHeader toggle={closeEditor}>
          {editor?.axis === 'columns' ? t('Configure Column') : t('Configure Row')}
        </ModalHeader>
        <ModalBody>
          {selectedEntry && (
            <div className="d-flex flex-wrap gap-3 text-muted small mb-3">
              <span>{`${t('Stable ID')}: ${selectedEntry.id}`}</span>
              {selectedCoordinate && (
                <span>
                  {`${selectedCoordinate.axis} ${t('Coordinate')}: ${selectedCoordinate.value}`}
                </span>
              )}
            </div>
          )}
          <FormGroup>
            <Label for="warehouse-axis-label">{t('Display Label')}</Label>
            <Input
              id="warehouse-axis-label"
              maxLength="45"
              placeholder={editor?.axis === 'columns'
                ? t('Example: X')
                : t('Example: 5')}
              value={editor?.label || ''}
              onChange={(event) => setEditor((current) => ({
                ...current,
                label: event.target.value,
              }))}
            />
          </FormGroup>
          <FormGroup>
            <Label for="warehouse-axis-size">
              {editor?.axis === 'columns' ? t('Column Width') : t('Row Height')}
            </Label>
            <Input
              id="warehouse-axis-size"
              max={MAX_AXIS_SIZE}
              min={MIN_AXIS_SIZE}
              step="0.1"
              type="number"
              value={editor?.size ?? 1}
              onChange={(event) => setEditor((current) => ({
                ...current,
                size: event.target.value,
              }))}
            />
            <div className="form-text">
              {t('Relative scale units. Location access remains centered in its aisle.')}
            </div>
          </FormGroup>
          <FormGroup>
            <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
              <Label className="mb-0" for="warehouse-axis-pattern-0">
                {t('Location Regex Patterns')}
              </Label>
              <Button
                color="primary"
                disabled={(editor?.patterns?.length || 0) >= MAX_AXIS_PATTERNS}
                onClick={addAxisPattern}
                outline
                size="sm"
                type="button"
              >
                <i className="mdi mdi-plus me-1" />
                {t('Add Regex')}
              </Button>
            </div>
            {(editor?.patterns || []).map((pattern, index) => (
              <div className="d-flex align-items-center gap-2 mb-2" key={pattern.id}>
                <Input
                  aria-label={`${t('Location Regex')} ${index + 1}`}
                  id={`warehouse-axis-pattern-${index}`}
                  maxLength="500"
                  placeholder={editor?.axis === 'columns'
                    ? '^\\d+\\.X\\.'
                    : '^5\\.'}
                  value={pattern.value}
                  onChange={(event) => updateAxisPattern(index, event.target.value)}
                />
                <Button
                  aria-label={t('Remove Regex')}
                  color="danger"
                  onClick={() => removeAxisPattern(index)}
                  outline
                  size="sm"
                  type="button"
                >
                  <i className="mdi mdi-close" />
                </Button>
              </div>
            ))}
            <div className="form-text">
              {t(
                'Patterns are evaluated case-insensitively with OR logic. At least one row pattern and one column pattern must match a location.',
              )}
            </div>
          </FormGroup>
          <FormGroup className="mb-0">
            <Label for="warehouse-axis-test-label">{t('Test Location Label')}</Label>
            <Input
              id="warehouse-axis-test-label"
              placeholder="12.Y.4.C"
              value={testLabel}
              onChange={(event) => setTestLabel(event.target.value)}
            />
            {patternTest !== null && (
              <div className={`mt-2 fw-semibold ${patternTest ? 'text-success' : 'text-danger'}`}>
                {patternTest ? t('Pattern matches') : t('Pattern does not match')}
              </div>
            )}
          </FormGroup>
        </ModalBody>
        <ModalFooter className="d-flex flex-wrap justify-content-between">
          <div className="d-flex flex-wrap gap-2">
            <Button color="secondary" outline onClick={() => insertAxis('before')}>{t('Insert Before')}</Button>
            <Button color="secondary" outline onClick={() => insertAxis('after')}>{t('Insert After')}</Button>
            <Button color="danger" outline onClick={removeAxis}>{t('Remove')}</Button>
          </div>
          <div className="d-flex gap-2">
            <Button color="secondary" onClick={closeEditor}>{t('Cancel')}</Button>
            <Button color="primary" onClick={saveAxisRule}>{t('Apply')}</Button>
          </div>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={annotationEditor !== null}
        toggle={closeAnnotationEditor}
        zIndex={1080}
        centered
      >
        <ModalHeader toggle={closeAnnotationEditor}>{t('Area Annotation')}</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="warehouse-annotation-title">{t('Title')}</Label>
            <Input
              id="warehouse-annotation-title"
              maxLength="90"
              placeholder={t('Example: Forklift staging')}
              value={annotationEditor?.title || ''}
              onChange={(event) => setAnnotationEditor((current) => ({
                ...current,
                title: event.target.value,
              }))}
            />
          </FormGroup>
          <FormGroup>
            <Label for="warehouse-annotation-note">{t('Note')}</Label>
            <Input
              id="warehouse-annotation-note"
              maxLength="1000"
              placeholder={t('Visible to people viewing and editing the map.')}
              rows="4"
              type="textarea"
              value={annotationEditor?.note || ''}
              onChange={(event) => setAnnotationEditor((current) => ({
                ...current,
                note: event.target.value,
              }))}
            />
          </FormGroup>
          <FormGroup className="mb-0">
            <Label for="warehouse-annotation-color">{t('Highlight Color')}</Label>
            <Input
              id="warehouse-annotation-color"
              type="select"
              value={annotationEditor?.color || ANNOTATION_COLORS[0].value}
              onChange={(event) => setAnnotationEditor((current) => ({
                ...current,
                color: event.target.value,
              }))}
            >
              {ANNOTATION_COLORS.map((color) => (
                <option key={color.value} value={color.value}>{t(color.label)}</option>
              ))}
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <div>
            {(map.definition.annotations || []).some(
              (annotation) => annotation.id === annotationEditor?.id,
            ) && (
              <Button color="danger" outline onClick={removeAnnotation}>
                {t('Delete Annotation')}
              </Button>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button color="secondary" onClick={closeAnnotationEditor}>{t('Cancel')}</Button>
            <Button color="primary" onClick={saveAnnotation}>{t('Apply')}</Button>
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
}

WarehouseMap.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(WarehouseMap);
